import { useMemo } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { LoadingScreen } from '@/features/check/StatusScreen';
import { PaymentMethodScreen } from '@/features/payment/PaymentMethodScreen';
import { startApplePaySheetFor } from '@/features/payment/wallets/applePaySheet';
import { useWalletCapabilities } from '@/features/payment/wallets/useWalletCapabilities';
import { loadDraft, saveDraft } from '@/payment/draft';
import { useCheckout } from './checkoutContext';

/**
 * How the guest wants to pay.
 *
 * Only routed to when there is more than one answer; `TipRoute` skips straight past it otherwise.
 */
export function PaymentRoute() {
    const { location, check } = useCheckout();
    const navigate = useNavigate();

    const draft = useMemo(() => loadDraft(check.id), [check.id]);

    // Already answered in the normal case - the tip step started this probe while the guest was choosing.
    const capabilities = useWalletCapabilities(location);

    /*
     * Reached without a tip - a typed URL, a stale bookmark, or storage cleared mid-flow. The footer here
     * quotes a total that includes the tip, so rendering with an assumed zero would show a guest a figure
     * they never agreed to. Send them back to decide it.
     */
    if (!draft) return <Navigate to="/tip" replace />;

    /*
     * Waiting rather than rendering with wallets assumed absent. A list that showed Card alone and then grew
     * an Apple Pay row a moment later is worse than a brief wait: a guest reaching for one control as another
     * appears under their thumb can tap the wrong one.
     */
    if (capabilities.isPending) return <LoadingScreen />;

    return (
        <PaymentMethodScreen
            location={location}
            check={check}
            tipCents={draft.tipCents}
            capabilities={capabilities.data ?? { applePay: false, googlePay: false }}
            onContinue={(method) => {
                /*
                 * Before the navigation and before anything is awaited: Safari will only build an
                 * ApplePaySession inside the synchronous part of the gesture that asked for it. This is the
                 * tap - the guest chose Apple Pay on this screen - so the sheet opens from here and the
                 * payment step takes it over. See `applePaySheet.ts`.
                 */
                if (method === 'applePay') {
                    startApplePaySheetFor(location, check.amountDueCents + draft.tipCents);
                }

                saveDraft({ ...draft, method });
                navigate('/pay');
            }}
            // Back to the tip step, unless this location does not take tips - that route forwards straight
            // here, so sending them to it would be a Back button that does nothing.
            onBack={() => navigate(check.tipsEnabled ? '/tip' : '/check')}
        />
    );
}
